import React from "react";
import styled, { css } from "styled-components";
import * as icons from "./svg";

export type IconName = keyof typeof icons;
export type IconSize = "micro" | "mini" | "small" | "medium" | "large";

export interface IconProps {
  iconName: IconName;
  size?: IconSize;
  noHover?: boolean;
  noSize?: boolean;
  variant?: "filled" | "outline";
  color?: string;
}

const sizeMap: Record<IconSize, number> = {
  micro: 14,
  mini: 16,
  small: 18,
  medium: 24,
  large: 32,
};

const Icon: React.FC<IconProps> = ({
  iconName,
  size = "small",
  noHover = false,
  noSize = false,
  variant,
  color = "var(--icon-default)",
}) => {
  const IconComponent = icons[iconName];

  if (!IconComponent) {
    console.error(`Icon "${iconName}" does not exist in the icons object.`);
    return null;
  }

  return (
    <StyledIconWrapper
      size={sizeMap[size]}
      $noHover={noHover}
      $noSize={noSize}
      $variant={variant ?? "filled"}
      $color={color}
    >
      <IconComponent />
    </StyledIconWrapper>
  );
};

const StyledIconWrapper = styled.span<{
  size: number;
  $noHover: boolean;
  $noSize: boolean;
  $variant: "filled" | "outline";
  $color: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color} !important;

  ${({ $noSize, size }) =>
    !$noSize &&
    css`
      width: ${size}px;
      height: ${size}px;
    `}

  svg {
    ${({ $noSize, size }) =>
      !$noSize &&
      css`
        width: ${size}px !important;
        height: ${size}px !important;
      `}
  }

  svg path {
    ${({ $variant, $color }) =>
      $variant === "outline"
        ? css`
            fill: none !important;
            stroke: ${$color} !important;
          `
        : css`
            fill: ${$color} !important;
          `}
  }

  ${({ $noHover }) =>
    !$noHover &&
    css`
      &:hover {
        svg path {
          stroke: var(--icon-hover) !important;
        }
      }
    `}

  &[disabled] {
    pointer-events: none;

    svg path {
      fill: var(--icon-deactivated) !important;
      stroke: var(--icon-deactivated) !important;
    }
  }
`;

export default Icon;
